import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { SELECT_VARIANTS, SIZES } from '../../constants/constants';
import { Icons } from '../../icons/Icons';
import { getQuickFilterSearchOptionLabel, isQuickFilterSearchOptionEqual, QuickFilterSearchOption } from './filters';
import { FilterItemProps } from './QuickFilterItem';

export default function QuickFilterSearchMultiSelectItem({
    filter,
    onFilterChanged,
    requiredFilters,
    sx = {}
}: FilterItemProps) {
    const options = useMemo<QuickFilterSearchOption[]>(
        () => filter.options.map((option) => ({ label: option })),
        [filter.options]
    );
    const isRequired = requiredFilters.includes(filter.id);
    const [selectedValues, setSelectedValues] = useState<QuickFilterSearchOption[]>(() =>
        (filter.values || []).map((value) => ({ label: value }))
    );

    useEffect(() => {
        setSelectedValues((filter.values || []).map((value) => ({ label: value })));
    }, [filter.id, filter.values]);

    const handleChange = useCallback(
        (_event: SyntheticEvent, newValues: QuickFilterSearchOption[]) => {
            if (isRequired && newValues.length === 0) {
                return;
            }
            setSelectedValues(newValues);
            filter.values = newValues.map((option) => option.label);
            onFilterChanged(filter);
        },
        [filter, isRequired, onFilterChanged]
    );

    const renderValue = useCallback(
        (value: QuickFilterSearchOption[], getItemProps: (params: { index: number }) => Record<string, unknown>) =>
            value.map((option, index) => {
                const itemProps = getItemProps({ index });
                if (isRequired && value.length === 1) {
                    const { onDelete, ...restItemProps } = itemProps;
                    return (
                        <Chip {...restItemProps} key={`${option.label}-${index}`} label={option.label} size="small" />
                    );
                }
                return <Chip {...itemProps} key={`${option.label}-${index}`} label={option.label} size="small" />;
            }),
        [isRequired]
    );

    return (
        <Autocomplete
            multiple
            limitTags={1}
            sx={{
                width: 200,
                '& .MuiInputBase-root:not(.Mui-focused)': { flexWrap: 'nowrap' },
                ...(sx || {})
            }}
            size={SIZES.small}
            disableCloseOnSelect
            options={options}
            value={selectedValues}
            isOptionEqualToValue={isQuickFilterSearchOptionEqual}
            getOptionLabel={getQuickFilterSearchOptionLabel}
            renderValue={renderValue}
            renderOption={(props, option, { selected: isSelected }) => (
                <li {...props} key={props.id}>
                    <Checkbox
                        icon={<Icons.CheckboxBlank fontSize="small" />}
                        checkedIcon={<Icons.Checkbox fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={isSelected}
                    />
                    {option.label}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant={SELECT_VARIANTS.standard}
                    placeholder={selectedValues.length === 0 ? filter.label : undefined}
                />
            )}
            onChange={handleChange}
        />
    );
}
